import { HttpClient } from '@angular/common/http';
import {distinctUntilChanged, map, share} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {getMessageFromException} from '../utilities/Util';
import {Agency, AgencyClient, PagedListOfAgency} from './api.client';
import {NotificationService} from './NotificationService';
import {BehaviorSubject, Observable} from 'rxjs';
import {StateResetRegistry} from './state-reset-registry.service';

// 003-retire-ngrx (US3): the agency slice moved off NgRx into this service.
// allAgencies is the cross-route lookup (pledge/user/application dropdowns +
// AgenciesCheckbox) — a load-once cache mirroring allPledgeTypes, refreshed by
// add/update/delete so lookups never go stale after an admin edit. The PAGED
// search results are NOT held here: the admin list and the agency-picker modal
// each hold their own page (service-held results were last-writer-wins across
// pages and outlived the criteria that produced them). Two fields did NOT come
// off the store: isAgenciesLoading had no selector (write-only — not ported),
// and agencyEfnCases moved to EfnService, the service that actually produces
// it (its old selector was even named selectEfnDataAgencyEfnCases).
interface AgencySliceState {
    currentSelectedAgency: Agency;
    allAgencies: Agency[];
}

const initialState: AgencySliceState = {
    currentSelectedAgency: null,
    allAgencies: null
};

// searchAgencies is the only list endpoint (no non-paged variant); this limit
// just needs to exceed the real agency count.
const ALL_AGENCIES_LIMIT = 1000;

@Injectable()
export class AgencyService {

    private readonly _state$ = new BehaviorSubject<AgencySliceState>(initialState);

    // ---------------- Streams (former agency selectors) ----------------
    readonly allAgencies$ = this.select((s) => s.allAgencies);
    readonly currentSelectedAgency$ = this.select((s) => s.currentSelectedAgency);

    constructor(private _http: HttpClient,
                private _agencyApiService: AgencyClient,
                private _notificationService: NotificationService,
                resetRegistry: StateResetRegistry) {
        resetRegistry.register(() => this._state$.next(initialState));
    }

    getAgencies(
        searchValue: string,
        pledgeTypeId: string,
        includeDisabled: boolean = false,
        limit: number,
        sortName: string,
        descending: boolean,
        offset: number): Observable<PagedListOfAgency> {
        const request = this._agencyApiService.searchAgencies(
            searchValue,
            pledgeTypeId,
            includeDisabled,
            limit || 10,
            offset || 0,
            sortName || '',
            null,
            descending || false
        ).pipe(share());
        request.subscribe({
            error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }
        });
        return request;
    }

    // Load-once reference cache (all agencies, disabled included — consumers
    // filter isActive as needed, same contract as allPledgeTypes).
    getAllAgencies(): Observable<PagedListOfAgency> {
        const request = this._agencyApiService.searchAgencies(
            '',
            null,
            true,
            ALL_AGENCIES_LIMIT,
            0,
            'AgencyName',
            null,
            false
        ).pipe(share());
        request.subscribe({
            next: (data) => {
                this.setAllAgenciesAction(data?.items ?? []);
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }
        });
        return request;
    }

    addAgency(agency: Agency): Observable<Agency> {
        const request = this._agencyApiService.addAgency(agency).pipe(share());
        this._notificationService.showSuccess('Attempting to save new Agency.');
        request.subscribe({
            next: (result) => {
                this.setCurrentSelectedAgency(result);
                this.refreshAllAgencies();
                this._notificationService.showSuccess('Agency Successfully Created!');
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }, complete: () => {

            }
        });

        return request;
    }

    updateAgency(agency: Agency): Observable<Agency> {
        const request = this._agencyApiService.updateAgency(agency).pipe(share());
        this._notificationService.showSuccess('Attempting to update Agency.');
        request.subscribe({
            next: (result) => {
                this.setCurrentSelectedAgency(result);
                this.refreshAllAgencies();
                this._notificationService.showSuccess('Agency Successfully Updated!');
            }, error: () => {
                this._notificationService.showError('There was a problem updating this Agency.');
            }, complete: () => {

            }
        });

        return request;
    }

    deleteAgency(id: string): Observable<boolean> {
        const request = this._agencyApiService.deleteAgency(id).pipe(share());
        this._notificationService.showSuccess('Attempting to delete Agency.');
        request.subscribe({
            next: () => {
                this.refreshAllAgencies();
                this._notificationService.showSuccess('Agency Successfully Deleted!');
            },
            error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            },
            complete: () => {

            }
        });

        return request;
    }

    setCurrentSelectedAgency(selectedAgency: Agency): void {
        this.setCurrentSelectedAgencyAction(selectedAgency);
    }

    getAgencyById(id: string): Observable<Agency> {
        const request = this._agencyApiService.retrieveAgency(id).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setCurrentSelectedAgency(result);
            }, error: () => {
                this._notificationService.showError('There was a problem retrieving the selected agency.');
            }, complete: () => {

            }
        });
        return request;
    }

    getRegionAdminAgencies(): Observable<Agency[]> {
        return this._agencyApiService.getRegionAdminAgencies().pipe(share());
    }

    // -------- state transitions (former agency actions) ----------
    setCurrentSelectedAgencyAction(selectedAgency: Agency): void {
        this.patch({currentSelectedAgency: selectedAgency});
    }

    private setAllAgenciesAction(allAgencies: Agency[]): void {
        this.patch({allAgencies});
    }

    // Only refetch after a mutation if something loaded the cache already —
    // otherwise the next consumer's load-if-empty fetch gets fresh data anyway.
    private refreshAllAgencies(): void {
        if (this._state$.value.allAgencies) {
            this.getAllAgencies();
        }
    }

    // ---------------- State plumbing ----------------
    private select<T>(project: (state: AgencySliceState) => T): Observable<T> {
        return this._state$.pipe(map(project), distinctUntilChanged());
    }

    private patch(partial: Partial<AgencySliceState>): void {
        this._state$.next({...this._state$.value, ...partial});
    }
}
